import styled from 'styled-components';

import {
  U21ButtonGroup,
  U21FormField,
  U21FormFieldTypes,
  U21Spacer,
  U21Typography,
  useU21Form,
  useU21FormState,
} from 'app/shared/u21-ui/components';

import { getRiskCategoryStyles } from 'app/modules/riskRatings/helpers';
import { MAX_RISK, MIN_RISK } from 'app/modules/riskRatings/constants';
import { RiskLevel } from 'app/modules/riskRatings/models';

interface Props {
  highRiskFloor: number;
  lowRiskCeiling: number;
}

const getTierForScore = (
  score: number,
  highRiskFloor: number,
  lowRiskCeiling: number,
): RiskLevel => {
  if (score > highRiskFloor) {
    return 'HIGH';
  }
  if (score < lowRiskCeiling) {
    return 'LOW';
  }
  return 'MEDIUM';
};

const getDefaultScoreForTier = (
  tier: RiskLevel,
  highRiskFloor: number,
  lowRiskCeiling: number,
): number => {
  switch (tier) {
    case 'LOW':
      return 1;
    case 'HIGH':
      return 99;
    case 'MEDIUM':
    default:
      return Math.round((lowRiskCeiling + highRiskFloor) / 2);
  }
};

export const RiskScorePicker = ({ highRiskFloor, lowRiskCeiling }: Props) => {
  const { change } = useU21Form<{ score: number }>();
  const { values } = useU21FormState<{ score: number }>();

  const tier = getTierForScore(values.score, highRiskFloor, lowRiskCeiling);

  const riskStyles = getRiskCategoryStyles(
    values.score,
    highRiskFloor,
    lowRiskCeiling,
  );

  return (
    <U21Spacer>
      <U21Spacer>
        <U21Typography variant="subtitle2">Risk level</U21Typography>
        <U21ButtonGroup<RiskLevel>
          buttons={[
            { label: 'Low', value: 'LOW' },
            { label: 'Medium', value: 'MEDIUM' },
            { label: 'High', value: 'HIGH' },
          ]}
          value={tier}
          onClick={(selectedTier) => {
            change(
              'score',
              getDefaultScoreForTier(
                selectedTier,
                highRiskFloor,
                lowRiskCeiling,
              ),
            );
          }}
          color={riskStyles.colorSchema}
        />
      </U21Spacer>
      <StyledU21FormFieldSlider
        name="score"
        type={U21FormFieldTypes.SLIDER}
        fieldProps={{
          min: MIN_RISK,
          max: MAX_RISK,
          step: 1,
          marks: [
            { value: MIN_RISK, label: '0' },
            {
              value: lowRiskCeiling,
              label: (
                <MediumText aria-label={MEDIUM_FLOOR_ARIA_LABEL}>
                  {lowRiskCeiling} Medium
                </MediumText>
              ),
            },
            {
              value: highRiskFloor,
              label: (
                <HighText aria-label={HIGH_FLOOR_ARIA_LABEL}>
                  {highRiskFloor} High
                </HighText>
              ),
            },
            { value: MAX_RISK, label: '100' },
          ],
          valueLabelDisplay: 'on',
          color: riskStyles.colorSchema,
        }}
      />
    </U21Spacer>
  );
};

const MEDIUM_FLOOR_ARIA_LABEL = 'Medium floor';
const HIGH_FLOOR_ARIA_LABEL = 'High floor';

const MediumText = styled.span`
  color: ${(props) => props.theme.palette.warning.dark};
`;

const HighText = styled.span`
  color: ${(props) => props.theme.palette.error.main};
`;

const StyledU21FormFieldSlider = styled(U21FormField)`
  .MuiSlider-mark:has(
    + .MuiSlider-markLabel [aria-label='${MEDIUM_FLOOR_ARIA_LABEL}']
  ) {
    width: 4px;
    height: 12px;
    background-color: ${({ theme }) => theme.palette.warning.main};
  }

  .MuiSlider-mark:has(
    + .MuiSlider-markLabel [aria-label='${HIGH_FLOOR_ARIA_LABEL}']
  ) {
    width: 4px;
    height: 12px;
    background-color: ${({ theme }) => theme.palette.error.main};
  }
`;
